import { Table } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Row } from 'react-table'

import { TableIds } from '../../../../enums/tableIds'
import { getTableColumnIdDirection } from '../../../../utils/getTableColumnIdDirection'
import { ReviewEntriesViewFilter } from '../../enums/reviewEntriesViewFilter'
import { useReviewEntriesHiddenColumns } from '../../hooks/useReviewEntriesHiddenColumns'
import { ReviewEntriesTableSelection, useReviewEntriesTableColumns } from '../../hooks/useReviewEntriesTableColumns'
import type { UseReviewEntriesTableFiltersResult } from '../../hooks/useReviewEntriesTableFilters'
import type { ReviewEntryTableRow } from '../../types/reviewEntryTableRow'
import { getReviewEntriesOddDocumentRowIds } from '../../utils/getReviewEntriesOddDocumentRowIds'
import {
  DEFAULT_REVIEW_ENTRIES_SORTED_COLUMN_ID,
  sortReviewEntriesTableRows,
} from '../../utils/sortReviewEntriesTableRows'
import { ReviewEntriesFiltersBar } from '../ReviewEntriesFiltersBar'
import { ReviewEntriesTableToolbar } from '../ReviewEntriesTableToolbar'
import { ReviewEntriesEmptyQueueState } from './elements/ReviewEntriesEmptyQueueState'
import * as Styled from './styles'
import { getReviewEntriesEmptyRowInfo } from './utils/getReviewEntriesEmptyRowInfo'

interface ReviewEntriesTableProps {
  isError: boolean
  isLoading: boolean
  onRowClick?: (row: Row<ReviewEntryTableRow>) => void
  selection?: ReviewEntriesTableSelection
  selectionBar?: ReactNode
  sourceRows: ReviewEntryTableRow[]
  tableFilters: UseReviewEntriesTableFiltersResult
}

export const ReviewEntriesTable = ({
  isError,
  isLoading,
  onRowClick,
  selection,
  selectionBar,
  sourceRows,
  tableFilters,
}: ReviewEntriesTableProps): ReactElement => {
  const { t } = useTranslation()
  const columns = useReviewEntriesTableColumns({ selection })
  const [sortedColumnId, setSortedColumnId] = useState(DEFAULT_REVIEW_ENTRIES_SORTED_COLUMN_ID)
  const [hiddenColumnIds = [], setHiddenColumnIds] = useReviewEntriesHiddenColumns()
  const {
    activeTypes = [],
    addedTypes,
    autoOpenFilterType,
    clearAutoOpenFilterType,
    filteredRows,
    filters,
    isFiltersBarVisible,
    addFilter,
    removeFilter,
    resetAllFilters,
    resetFilter,
    searchQuery = '',
    setFilterValue,
    setSearchQuery,
    setViewFilter,
    toValidateCount,
    viewFilter = ReviewEntriesViewFilter.ToValidate,
  } = tableFilters

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
  }, [setSearchQuery])

  const handleSort = useCallback((columnId: string, isDesc: boolean) => {
    setSortedColumnId(getTableColumnIdDirection(columnId, isDesc) ?? '')
  }, [])

  const sortedRows = useMemo(
    () => sortReviewEntriesTableRows(filteredRows, sortedColumnId),
    [filteredRows, sortedColumnId],
  )
  const oddDocumentRowIds = useMemo(() => getReviewEntriesOddDocumentRowIds(sortedRows), [sortedRows])
  const visibleColumns = useMemo(
    () =>
      columns.filter((column) => {
        const columnId = String(column.id ?? column.accessor ?? '')

        return !hiddenColumnIds.includes(columnId)
      }),
    [columns, hiddenColumnIds],
  )

  const emptyRowKind = useMemo(
    () =>
      getReviewEntriesEmptyRowInfo({
        filteredRowCount: filteredRows.length,
        hasNarrowingFilters: searchQuery.trim().length > 0 || activeTypes.length > 0,
        isError,
        isLoading,
        sourceRowCount: sourceRows.length,
        viewFilter,
      }),
    [activeTypes.length, filteredRows.length, isError, isLoading, searchQuery, sourceRows.length, viewFilter],
  )

  const emptyRowInfo = useMemo(() => {
    if (emptyRowKind === 'empty-queue') {
      return <ReviewEntriesEmptyQueueState />
    }

    if (emptyRowKind === 'filtered') {
      return t('review_entries.empty.filtered')
    }

    if (emptyRowKind === 'error') {
      return t('review_entries.error.loading')
    }

    return undefined
  }, [emptyRowKind, t])

  return (
    <>
      <ReviewEntriesTableToolbar
        addedFilterTypes={addedTypes}
        hiddenColumnIds={hiddenColumnIds}
        onAddFilter={addFilter}
        onHiddenColumnIdsChange={setHiddenColumnIds}
        onSearchChange={setSearchQuery}
        onSearchClear={handleSearchClear}
        onViewFilterChange={setViewFilter}
        searchQuery={searchQuery}
        toValidateCount={toValidateCount}
        viewFilter={viewFilter}
      />
      <ReviewEntriesFiltersBar
        addedTypes={addedTypes}
        addFilter={addFilter}
        autoOpenFilterType={autoOpenFilterType}
        clearAutoOpenFilterType={clearAutoOpenFilterType}
        filters={filters}
        isVisible={isFiltersBarVisible}
        removeFilter={removeFilter}
        resetAllFilters={resetAllFilters}
        resetFilter={resetFilter}
        rows={sourceRows}
        setFilterValue={setFilterValue}
      />
      {selectionBar}
      <Styled.ReviewEntriesTableWrapper oddDocumentRowIds={oddDocumentRowIds}>
        <Table
          columns={visibleColumns}
          data={sortedRows}
          emptyRowInfo={emptyRowInfo}
          id={TableIds.ReviewEntries}
          isLoading={isLoading && !isError}
          onRowClick={onRowClick}
          onSort={handleSort}
          sortedColumnId={sortedColumnId}
          withColumnsFiltering={false}
          withPagination={false}
          withStickyHeader
        />
      </Styled.ReviewEntriesTableWrapper>
    </>
  )
}
